import { GO_LIVE_SECTIONS, PENDING, SUPPORTED_LANGUAGES } from './types';

export interface BrandValidation {
  ok: boolean;
  errors: string[];
}

type Raw = Record<string, unknown>;

/** Required string fields, by dotted path. Optional ones are not listed. */
const REQUIRED: readonly string[] = [
  'id', 'defaultLanguage', 'productName', 'shortName', 'staffAppName', 'kitchenDisplayName', 'voiceWakePhrase',
  'colors.primary', 'colors.primaryDark', 'colors.accent', 'colors.background', 'colors.text',
  'fonts.heading', 'fonts.body',
  'assets.logo', 'assets.logoDark', 'assets.icon', 'assets.favicon', 'assets.emailHeader', 'assets.splash',
  'domains.console', 'domains.guest', 'domains.admin', 'domains.marketing', 'domains.api',
  'email.fromName', 'email.fromAddress', 'email.staffFromAddress', 'email.stockFromAddress',
  'email.replyTo', 'email.supportAddress',
  'support.email',
  'company.legalName', 'company.address', 'company.vatOrRegistrationNumber', 'company.country',
  'company.privacyControllerEmail',
  'legal.termsUrl', 'legal.privacyUrl',
  'fiscal.softwareName', 'fiscal.producerName',
  'app.slug', 'app.bundleId', 'app.androidPackage', 'app.easProjectId', 'app.easOwner',
  'app.updatesUrl', 'app.sentryProject',
];

const COLOR = /^#[0-9A-Fa-f]{6}$/;
const HOSTNAME = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HTTPS_ORIGIN = /^https:\/\/[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;
const SECRET_KEY = /(key|secret|token|password)/i;

function isObject(value: unknown): value is Raw {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function read(raw: Raw, path: string): unknown {
  return path.split('.').reduce<unknown>((node, part) => (isObject(node) ? node[part] : undefined), raw);
}

function isGoLive(path: string): boolean {
  return GO_LIVE_SECTIONS.some((section) => path.startsWith(`${section}.`));
}

function collectSecretKeys(node: unknown, prefix: string, out: string[]): void {
  if (!isObject(node)) return;
  for (const [key, value] of Object.entries(node)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (SECRET_KEY.test(key)) out.push(`${path}: secrets are not allowed in a brand`);
    collectSecretKeys(value, path, out);
  }
}

function checkStrings(raw: Raw, errors: string[]): void {
  for (const path of REQUIRED) {
    const value = read(raw, path);
    if (typeof value !== 'string' || value.trim() === '') {
      errors.push(`${path}: required`);
    } else if (value === PENDING && !isGoLive(path)) {
      errors.push(`${path}: TODO is allowed only in go-live fields`);
    }
  }
}

function checkFormats(raw: Raw, errors: string[]): void {
  const language = read(raw, 'defaultLanguage');
  if (typeof language === 'string' && language.trim() !== '' && !(SUPPORTED_LANGUAGES as readonly string[]).includes(language)) {
    errors.push(`defaultLanguage: must be one of ${SUPPORTED_LANGUAGES.join(', ')}`);
  }
  const colors = read(raw, 'colors');
  if (isObject(colors)) {
    for (const [name, value] of Object.entries(colors)) {
      if (typeof value === 'string' && !COLOR.test(value)) errors.push(`colors.${name}: must be #RRGGBB`);
    }
  }
  const domains = read(raw, 'domains');
  if (isObject(domains)) {
    for (const [name, value] of Object.entries(domains)) {
      if (typeof value === 'string' && value.trim() !== '' && !HOSTNAME.test(value)) {
        errors.push(`domains.${name}: must be a bare hostname`);
      }
    }
  }
}

function checkSales(raw: Raw, errors: string[]): void {
  const sales = read(raw, 'sales');
  if (sales === undefined) return;
  if (!isObject(sales)) {
    errors.push('sales: must be an object');
    return;
  }
  if (typeof sales.demoRequestInbox !== 'string' || !EMAIL.test(sales.demoRequestInbox)) {
    errors.push('sales.demoRequestInbox: must be an email address');
  }
  const origins = sales.marketingOrigins;
  if (!Array.isArray(origins) || origins.length === 0) {
    errors.push('sales.marketingOrigins: at least one origin');
    return;
  }
  for (const origin of origins) {
    if (typeof origin !== 'string' || !HTTPS_ORIGIN.test(origin)) {
      errors.push(`sales.marketingOrigins: ${String(origin)} must be an https origin without path`);
    }
  }
}

/** Checks a brand read from its data file. Pure; never throws. */
export function validateBrand(raw: unknown): BrandValidation {
  if (!isObject(raw)) return { ok: false, errors: ['brand: must be an object'] };
  const errors: string[] = [];
  checkStrings(raw, errors);
  checkFormats(raw, errors);
  checkSales(raw, errors);
  collectSecretKeys(raw, '', errors);
  return { ok: errors.length === 0, errors };
}
